import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"email" | "reset">("email");

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfo("");
    try {
      await resetPassword({ username: email });
      setMode("reset");
      setInfo(`We sent a 6-digit code to ${email}`);
    } catch (err: any) {
      // Don't leak whether an email is registered — show the same message
      // either way to prevent account enumeration.
      if (err.name === "UserNotFoundException") {
        setMode("reset");
        setInfo(`If an account exists for ${email}, we sent a code.`);
      } else if (err.name === "LimitExceededException") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.message || "Failed to send code");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setInfo("");
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      navigate("/login?reset=success");
    } catch (err: any) {
      if (err.name === "CodeMismatchException") {
        setError("That code doesn't match. Check your email and try again.");
      } else if (err.name === "ExpiredCodeException") {
        setError("That code expired. Send a new one.");
      } else if (err.name === "InvalidPasswordException") {
        setError(err.message);
      } else {
        setError(err.message || "Reset failed");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <svg width="44" height="44" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="fpLg1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4C6EF5"/><stop offset="100%" stopColor="#1A3CE8"/></linearGradient>
              <linearGradient id="fpLg2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#20C997"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
              <linearGradient id="fpBg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
            </defs>
            <rect width="52" height="52" rx="14" fill="url(#fpBg)"/>
            <g transform="translate(4,5)">
              <path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.95"/>
              <circle cx="11" cy="10" r="5" fill="url(#fpLg1)"/>
              <circle cx="11" cy="10" r="2.5" fill="white"/>
            </g>
            <g transform="translate(25,5)">
              <path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.85"/>
              <circle cx="11" cy="10" r="5" fill="url(#fpLg2)"/>
              <circle cx="11" cy="10" r="2.5" fill="white"/>
            </g>
          </svg>
          <span className="font-display text-2xl font-bold text-navy">Mouna</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md animate-slide-up">

          <div className="text-center mb-8">
            <h1 className="font-display text-3xl font-bold text-navy mb-2">
              {mode === "email" ? "Forgot password?" : "Reset password"}
            </h1>
            <p className="text-gray-500 text-sm">
              {mode === "email"
                ? "Enter your email and we'll send you a reset code"
                : "Enter the code we sent and choose a new password"}
            </p>
          </div>

          {info && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-2xl px-4 py-3 mb-5 animate-fade-in">
              {info}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-2xl px-4 py-3 mb-5 animate-fade-in">
              {error}
            </div>
          )}

          {mode === "email" && (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div className="form-section">
                <div className="form-group mb-0">
                  <label className="label">Email Address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com" required autoFocus className="input"/>
                </div>
              </div>

              <button type="submit" disabled={loading} className="btn btn-primary btn-full btn-lg">
                {loading ? "Sending..." : "Send Code"}
              </button>

              <div className="text-center text-sm text-gray-500">
                Remembered it?{" "}
                <button type="button" onClick={() => navigate("/login")}
                  className="text-mouna-blue font-semibold hover:underline">
                  Back to sign in
                </button>
              </div>
            </form>
          )}

          {mode === "reset" && (
            <form onSubmit={handleReset} className="space-y-4">
              <div className="form-section space-y-4">
                <div className="form-group mb-0">
                  <label className="label">Verification Code</label>
                  <input type="text" value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000" required autoFocus
                    className="input text-center text-2xl tracking-widest font-bold"/>
                </div>
                <div className="form-group mb-0">
                  <label className="label">New Password</label>
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder="Min 8 characters" required className="input"/>
                </div>
                <div className="form-group mb-0">
                  <label className="label">Confirm New Password</label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Repeat password" required className="input"/>
                </div>
              </div>

              <button type="submit" disabled={loading} className="btn btn-primary btn-full btn-lg">
                {loading ? "Resetting..." : "Reset Password"}
              </button>

              <div className="text-center text-sm text-gray-500">
                Didn't get the code?{" "}
                <button type="button"
                  onClick={() => { setMode("email"); setCode(""); setNewPassword(""); setConfirmPassword(""); setError(""); setInfo(""); }}
                  className="text-mouna-blue font-semibold hover:underline">
                  Send a new one
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}